/**
 * Shared plumbing for visualize client tools that mutate the current
 * workspace through PubSubBus panel-mutation actions.
 *
 * The bus invokes subscribers synchronously in the publisher's stack (a
 * documented contract on {@link AiAgentThreadPubSubBus.publish}), so the
 * workspace's result callback has already fired by the time `publish()`
 * returns — {@link runPanelMutation} exploits that to give tools
 * request/response semantics over a fire-and-forget bus.
 *
 * Also home to the batch-level coordination the same tools need: which of
 * them has navigated to the visualizer ({@link markNavigatedToVisualizer}),
 * and how the ones running behind that navigation wait for the workspace to
 * start listening instead of deep-linking a second one
 * ({@link runPanelMutationWhenReady}, {@link publishWhenVisualizerReady}).
 */
import type { TopicService } from "@/shared/domain/topics/TopicService";
import { resolveSignalEntries } from "@/shared/domain/workspaces/resolveSignalEntries";
import type {
  PanelMutationActionPayload,
  WorkspaceActionEntry,
  WorkspaceActionPayload,
  WorkspaceActionResult,
} from "@/shared/services/AiAgentThreadPubSubBus/models";
import { wait } from "@/shared/time";

import {
  type ClientToolBatchScope,
  type ClientToolDeps,
  type ClientToolExecutionResult,
  toolError,
  toolSuccess,
} from "../ClientTools";

/**
 * Upper bound on message paths per tool call. Keeps a single call from
 * flooding one panel (and the per-id record fetches) while comfortably
 * fitting real groupings like a 6-axis IMU.
 */
export const MAX_MESSAGE_PATHS_PER_CALL = 20;

/** True when `value` is an array of strings (the shape of id-list inputs). */
export function isStringArray(value: unknown): value is string[] {
  return (
    Array.isArray(value) && value.every((item) => typeof item === "string")
  );
}

export interface FetchedEntries {
  entries: WorkspaceActionEntry[];
  /**
   * Ids whose records could not be loaded, with per-id reasons, in the
   * snake_case shape tools surface as `invalid_paths`. Why every failing id
   * is reported (not just the first) is documented on
   * {@link resolveSignalEntries}.
   */
  invalid: { message_path_id: string; reason: string }[];
}

/**
 * Fetch the message path + topic record for every id, never throwing for
 * per-id failures — they come back in `invalid` so the caller can fail
 * atomically with per-path reasons. Delegates to
 * {@link resolveSignalEntries}, renaming its camelCase failure key to the
 * snake_case shape of {@link FetchedEntries}.
 */
export async function fetchEntries(
  topicService: TopicService,
  messagePathIds: string[],
): Promise<FetchedEntries> {
  const { entries, invalid } = await resolveSignalEntries(
    topicService,
    messagePathIds,
  );
  return {
    entries,
    invalid: invalid.map(({ messagePathId, reason }) => ({
      message_path_id: messagePathId,
      reason,
    })),
  };
}

/**
 * The error a tool returns when nothing is listening on the workspace bus.
 *
 * Retryable by design: the model's recovery is to call again on a following
 * turn, once the visualizer has finished loading.
 */
export const VISUALIZER_NOT_READY_ERROR =
  "The visualizer is not ready to receive workspace actions yet. " +
  "Wait for the visualizer page to finish loading, then try again.";

/**
 * How long a tool waits for the visualizer to start listening, and how often
 * it re-probes, when a tool earlier in its batch has already navigated there.
 *
 * There is no "workspace ready" event to await: the subscriber registers only
 * once the visualizer has hydrated its layout, and a `publish()` that returns
 * false is the only probe for it. The budget therefore covers a route
 * transition, the workspace's files/topics/message-path fetch, and applying
 * the layout — a large dataset or a cold backend pushes that past the usual
 * second or two, hence the slack. Timing out returns the retryable not-ready
 * error, and the batch latches it so no batch-mate pays the budget twice.
 */
const VISUALIZER_READY_POLL_INTERVAL_MS = 100;
const VISUALIZER_READY_TIMEOUT_MS = 10_000;

/**
 * Record that this tool has navigated to the visualizer, for the tools that
 * run after it in the same batch.
 *
 * Deps are captured once per batch and route closures with them, so a
 * batch-mate cannot otherwise learn that a navigation is in flight — it reads
 * the pre-batch route, concludes it is off the visualizer, and deep-links a
 * layout that replaces the one this navigation is carrying. See
 * {@link ClientToolBatchScope}.
 */
export function markNavigatedToVisualizer(deps: ClientToolDeps): void {
  if (deps.batchScope) {
    deps.batchScope.navigatedToVisualizer = true;
  }
}

/**
 * Whether a visualizer is available to this tool, and on what terms.
 *
 * The single predicate every visualize tool asks. `isOnVisualizerPage()` on
 * its own is not that predicate: the runner captures deps once per batch, so
 * the closure answers the route as it stood *before* the batch — it cannot
 * see a batch-mate's navigation, and it keeps answering `true` for a page
 * that batch-mate is in the middle of replacing.
 *
 * `"opening"` therefore outranks `"open"`. `client_visualize_open_file`
 * navigates to a fresh visualizer even when one is already up, so a tool
 * behind it that trusted `isOnVisualizerPage()` would publish into the
 * workspace being torn down and report success — the silent-success failure
 * this module exists to prevent.
 */
export type VisualizerStatus = "open" | "opening" | "unreachable" | "closed";

export function visualizerStatus(deps?: ClientToolDeps): VisualizerStatus {
  if (deps?.batchScope?.visualizerUnreachable === true) {
    return "unreachable";
  }
  if (deps?.batchScope?.navigatedToVisualizer === true) {
    return "opening";
  }
  return deps?.isOnVisualizerPage?.() === true ? "open" : "closed";
}

/** One publish attempt: whether the bus took it, and what it answered. */
interface PublishAttempt {
  published: boolean;
  result?: WorkspaceActionResult;
}

/**
 * Re-run `attempt` on a short interval until the bus takes it, or the budget
 * runs out.
 *
 * Retrying is safe because a publish with no subscriber is a no-op — the bus
 * hands the payload to nobody — so a re-publish cannot apply an action twice.
 */
async function untilVisualizerReady(
  attempt: () => PublishAttempt,
  scope?: ClientToolBatchScope,
): Promise<PublishAttempt> {
  const deadline = Date.now() + VISUALIZER_READY_TIMEOUT_MS;
  let outcome = attempt();
  while (!outcome.published && Date.now() < deadline) {
    await wait(VISUALIZER_READY_POLL_INTERVAL_MS);
    outcome = attempt();
  }
  // One batch-mate waiting out the budget is enough to conclude the
  // navigation is not arriving. The runner executes a batch serially, so
  // without this latch every later tool pays the full budget again to reach
  // the same answer — a four-tool batch spends 15s producing three identical
  // errors.
  if (!outcome.published && scope) {
    scope.visualizerUnreachable = true;
  }
  return outcome;
}

/**
 * Publish `payload`, retrying until the visualizer a batch-mate navigated to
 * is listening. Returns whether it was ever taken.
 */
export async function publishWhenVisualizerReady(
  publishWorkspaceAction: (payload: WorkspaceActionPayload) => boolean,
  payload: WorkspaceActionPayload,
  scope?: ClientToolBatchScope,
): Promise<boolean> {
  const { published } = await untilVisualizerReady(
    () => ({ published: publishWorkspaceAction(payload) }),
    scope,
  );
  return published;
}

function publishPanelMutation(
  publishWorkspaceAction: (payload: PanelMutationActionPayload) => boolean,
  buildPayload: (
    onResult: (result: WorkspaceActionResult) => void,
  ) => PanelMutationActionPayload,
): PublishAttempt {
  let result: WorkspaceActionResult | undefined;
  const published = publishWorkspaceAction(
    buildPayload((r) => {
      result = r;
    }),
  );
  return { published, result };
}

/**
 * Publish a panel-mutation workspace action and translate its synchronous
 * result into a client tool result.
 *
 * @param action - Action name echoed in the success response.
 * @param successExtra - Extra fields merged into the success response.
 */
export function runPanelMutation(
  publishWorkspaceAction: (payload: PanelMutationActionPayload) => boolean,
  action: string,
  buildPayload: (
    onResult: (result: WorkspaceActionResult) => void,
  ) => PanelMutationActionPayload,
  successExtra: Record<string, unknown> = {},
): ClientToolExecutionResult {
  return interpretPanelMutation(
    publishPanelMutation(publishWorkspaceAction, buildPayload),
    action,
    successExtra,
  );
}

/**
 * {@link runPanelMutation} for the case where the visualizer is still
 * mounting: waits for it to start listening rather than reporting it absent.
 *
 * Only for a tool whose batch-mate has already navigated. On this path the
 * alternative to waiting is a second deep link, and that is what loses a
 * panel: the route loader builds one fresh workspace per navigation, so
 * whichever layout lands second either aborts the first or is dropped by
 * `shouldRevalidate` and left stranded in sessionStorage.
 */
export async function runPanelMutationWhenReady(
  publishWorkspaceAction: (payload: PanelMutationActionPayload) => boolean,
  action: string,
  buildPayload: (
    onResult: (result: WorkspaceActionResult) => void,
  ) => PanelMutationActionPayload,
  successExtra: Record<string, unknown> = {},
  scope?: ClientToolBatchScope,
): Promise<ClientToolExecutionResult> {
  return interpretPanelMutation(
    await untilVisualizerReady(
      () => publishPanelMutation(publishWorkspaceAction, buildPayload),
      scope,
    ),
    action,
    successExtra,
  );
}

function interpretPanelMutation(
  { published, result }: PublishAttempt,
  action: string,
  successExtra: Record<string, unknown>,
): ClientToolExecutionResult {
  if (!published) {
    return toolError(VISUALIZER_NOT_READY_ERROR);
  }
  if (result === undefined) {
    return toolError("The visualizer did not report a result for this action.");
  }
  if (!result.ok) {
    return toolError(result.error, {
      ...(result.invalidPaths && {
        invalid_paths: result.invalidPaths.map((path) => ({
          message_path_id: path.messagePathId,
          reason: path.reason,
        })),
      }),
      // Kept apart from invalid_paths: a synthetic target is not a message
      // path id, and a model that read one out of a `message_path_id` field
      // would plausibly feed it back as one.
      ...(result.invalidTargets && {
        invalid_targets: result.invalidTargets.map((entry) => ({
          target: entry.target,
          reason: entry.reason,
        })),
      }),
    });
  }

  // panel_type tells the model what its signals are actually being rendered
  // as. Without it the only signal is the visualizer state a turn later, and a
  // panel type it did not expect reads as an unexplained outcome.
  return toolSuccess(action, {
    panel_id: result.panelId,
    ...(result.panelType ? { panel_type: result.panelType } : {}),
    ...result.details,
    ...successExtra,
  });
}
